/**
 * A single feedback ticket, opened straight from the link in a notification mail.
 *
 * The mail exists to say "we answered you"; landing on a dashboard would bury the answer.
 * This page puts the conversation front and centre with the reply box already there.
 *
 * The ticket is looked up among the caller's own tickets, so ownership needs no separate
 * check here: a ticket someone else reported simply is not in the list, and the page says
 * so plainly instead of leaking that the id exists.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getMyFeedbackTickets, replyToFeedbackTicket, type FeedbackTicket } from 'API/feedback';
import FeedbackThread from 'features/feedback/FeedbackThread';
import { Routes } from 'enums';

const STATUS_LABEL: Record<FeedbackTicket['status'], string> = {
  open: 'Offen',
  in_progress: 'In Arbeit',
  done: 'Erledigt',
};

const STATUS_COLOR: Record<FeedbackTicket['status'], 'default' | 'info' | 'success'> = {
  open: 'default',
  in_progress: 'info',
  done: 'success',
};

const FeedbackTicketPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState<FeedbackTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getMyFeedbackTickets();
      const found = (res.items || []).find((t) => t._id === id);
      setTicket(found || null);
    } catch {
      setLoadError('Das Ticket konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleReply = async (body: string) => {
    if (!id) return;
    const updated = await replyToFeedbackTicket(id, body);
    setTicket(updated);
  };

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(Routes.AI_CHAT)}
        size="small"
        sx={{ mb: 2 }}
      >
        Zurück
      </Button>

      {loading && (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      )}

      {!loading && loadError && (
        <Alert severity="error" action={<Button onClick={() => void load()}>Erneut versuchen</Button>}>
          {loadError}
        </Alert>
      )}

      {!loading && !loadError && !ticket && (
        <Alert severity="info">
          Dieses Ticket gehört nicht zu deinem Konto — oder es existiert nicht mehr. Falls du mit einem
          anderen Konto angemeldet bist, melde dich mit der Adresse an, an die die Benachrichtigung ging.
        </Alert>
      )}

      {!loading && ticket && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Stack direction="row" alignItems="flex-start" gap={1.5} sx={{ mb: 0.5 }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
              {ticket.title}
            </Typography>
            <Chip size="small" label={STATUS_LABEL[ticket.status]} color={STATUS_COLOR[ticket.status]} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Gemeldet am {new Date(ticket.createdAt).toLocaleDateString('de-DE')}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <FeedbackThread
            messages={ticket.messages}
            description={ticket.description}
            viewerRole="reporter"
            onReply={handleReply}
            autoScroll
          />
        </Paper>
      )}
    </Box>
  );
};

export default FeedbackTicketPage;
